import { debounceTime, distinctUntilChanged, map, Observable, of, startWith, switchMap } from 'rxjs';

import { Component, EventEmitter, Input, OnInit, Output, Self } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';

import { IAutocompleteOption } from 'app/models/filter.models';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { AsyncPipe, NgFor } from '@angular/common';

@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  imports: [MatFormField, MatInput, MatAutocompleteTrigger, MatAutocomplete, NgFor, MatOption, AsyncPipe],
})
export class AutocompleteComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = '';

  @Input() set options(value: string[]) {
    this.selectOptions = value.map((item) => ({
      id: item,
      value: item,
    }));

    this.filteredOptions =
      this.controlDir.control?.valueChanges.pipe(
        startWith(''),
        map((value) => this.innerFilter(value)),
      ) ?? of([]);
  }

  @Input() set filter(fn: (search: string) => Observable<IAutocompleteOption[]>) {
    this.filteredOptions =
      this.controlDir.control?.valueChanges.pipe(
        startWith(''),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((value) => {
          return fn(typeof value === 'string' ? value : value?.value);
        }),
      ) ?? of([]);
  }

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur: EventEmitter<void> = new EventEmitter<void>();

  onChange: (value: IAutocompleteOption) => void = () => {
    // no op
  };

  onTouched: () => void = () => {
    // no op
  };

  onValidationChange: () => void = () => {
    // no op
  };

  selectOptions: IAutocompleteOption[] = [];
  filteredOptions!: Observable<IAutocompleteOption[]>;
  selectedOption: IAutocompleteOption = {
    id: '',
    value: '',
  };

  constructor(@Self() public controlDir: NgControl) {
    controlDir.valueAccessor = this;
  }

  ngOnInit(): void {
    this.controlDir.control?.setValidators([this.validate]);
    this.controlDir.control?.updateValueAndValidity();
  }

  writeValue(selectedOption: IAutocompleteOption | null): void {
    this.selectedOption = {
      id: selectedOption?.id ?? '',
      value: selectedOption?.value ?? '',
    };
  }

  registerOnChange(onChange: (value: IAutocompleteOption) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  registerOnValidatorChange?(fn: () => void): void {
    this.onValidationChange = fn;
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const { id, value } = event.option;
    const selectedOption = { id, value };

    this.selectedOption = selectedOption;
    this.onChange(selectedOption);
  }

  validate = (control: AbstractControl): ValidationErrors | null => {
    if (typeof control.value === 'string' && control.value !== '') {
      return { incorrectAutocompleteValue: true };
    }

    return null;
  };

  private innerFilter(value: string | IAutocompleteOption): IAutocompleteOption[] {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value?.value?.toLowerCase();

    return this.selectOptions.filter((option) => option.value?.toLowerCase().includes(filterValue ?? ''));
  }
}
